import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import { updateSection } from '../../../redux/features/landingCmsSlice';
import ImageField from '../questionbank/ImageField';

/**
 * Section copy, imagery and the repeated cards inside each band of the page.
 *
 * ---------------------------------------------------------------------------
 * WHY A HEADING IS THREE BOXES
 *
 * Every heading on the landing page is "plain text, a gradient-filled phrase
 * with a highlighter behind it, then optionally more plain text":
 *
 *     Your child's report in [3 simple steps]
 *     [Fun Learning Zone] & AI Assistance
 *
 * The gradient and the highlighter are design and stay in the code. WHICH WORDS
 * get emphasised is editorial — it moves when the copy moves — so it has to be
 * editable. Three plain boxes with a live preview underneath beats inventing a
 * markup syntax the editor would have to learn, and beats guessing.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN EDITOR CANNOT BREAK
 *
 * Colours, gradients, blob shapes, animations and layout coordinates are not
 * here and never will be. Each card's visual treatment comes from the
 * component's own design array and is matched to the card BY POSITION, so
 * reordering cards reorders the words and never the look. That is what makes it
 * safe to hand this screen to someone who has never seen the code.
 */

/**
 * Sections that hold more than one KIND of item, told apart by `extra.group`.
 *
 * The hero's benefit lines and its key chips are one section because they are
 * one screenful; the footer's four link lists are one section because nobody
 * edits "resources" without looking at the rest of the footer. Both need the
 * editor to say which list an item belongs to — without this the group is
 * invisible, a new item silently joins the default list, and the only way to
 * add a fourth chip is a deploy.
 *
 * The groups themselves are declared in the frontend's
 * `src/data/landing/landingDefaults.js`; these are the labels for them.
 */
const BLOCK_GROUPS = {
  hero: [
    { value: 'benefit', label: 'Report benefit' },
    { value: 'fact', label: 'Key chip' },
  ],
  footer: [
    { value: 'social', label: 'Social link' },
    { value: 'quick', label: 'Quick link' },
    { value: 'resources', label: 'Resource link' },
    { value: 'contact', label: 'Contact detail' },
  ],
};

/**
 * Section-level `extra` keys that are real editable copy.
 *
 * `extra` is the escape hatch for the one or two fields a single section needs
 * and no other does — the emphasised phrase inside the hero's sub-line, the
 * label on a button that opens WhatsApp instead of the assessment. A pair of
 * columns that would be NULL on ten rows out of eleven is not worth the schema,
 * but the values are still copy, and copy an editor cannot reach is copy that
 * still needs a developer. Named boxes rather than a raw JSON field: nobody
 * should have to get a brace right to reword a button.
 */
const SECTION_EXTRA_FIELDS = {
  hero: [
    {
      key: 'subheadingAccent',
      label: 'Emphasised phrase in the body copy',
      helper: 'Rendered in bold, mid-sentence.',
    },
    { key: 'subheadingSuffix', label: 'Body copy after that phrase' },
    {
      key: 'secondaryCtaLabel',
      label: 'WhatsApp button label',
      helper: 'The second hero button. Opens WhatsApp on the number set in Settings.',
    },
  ],
  cta: [
    {
      key: 'secondaryCtaLabel',
      label: 'WhatsApp button label',
      helper: 'Opens WhatsApp on the number set in Settings.',
    },
  ],
};

const SectionPanel = ({ sections, revalidateSeconds }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.landingCms);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!sections.length) return;
    if (!selectedId) setSelectedId(sections[0].id);
  }, [sections, selectedId]);

  const selected = sections.find((s) => s.id === selectedId);

  useEffect(() => {
    if (!selected) return;
    setForm({
      isVisible: selected.isVisible,
      eyebrow: selected.eyebrow || '',
      heading: selected.heading || '',
      headingAccent: selected.headingAccent || '',
      headingSuffix: selected.headingSuffix || '',
      subheading: selected.subheading || '',
      ctaLabel: selected.ctaLabel || '',
      ctaHref: selected.ctaHref || '',
      media: selected.media || null,
      extra: selected.extra || null,
      blocks: (selected.blocks || []).map((b) => ({ ...b })),
    });
  }, [selected]);

  const groups = BLOCK_GROUPS[selected?.keyName] || null;
  const extraFields = SECTION_EXTRA_FIELDS[selected?.keyName] || null;

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const setExtra = (key, value) =>
    set({ extra: { ...(form.extra || {}), [key]: value } });

  const setBlock = (index, patch) =>
    set({ blocks: form.blocks.map((b, i) => (i === index ? { ...b, ...patch } : b)) });

  // A new item joins the FIRST declared group rather than none, because "no
  // group" is not a neutral state — the hero reads an ungrouped block as a
  // benefit line, so an item added with the intent of being a chip would
  // quietly appear in the wrong list. The dropdown beside it can move it.
  const addBlock = () =>
    set({
      blocks: [
        ...form.blocks,
        {
          title: '',
          body: '',
          eyebrow: '',
          icon: '',
          href: '',
          media: null,
          isVisible: true,
          ...(groups ? { extra: { group: groups[0].value } } : {}),
        },
      ],
    });

  const removeBlock = (index) => set({ blocks: form.blocks.filter((_, i) => i !== index) });

  const moveBlock = (index, delta) => {
    const next = [...form.blocks];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set({ blocks: next });
  };

  const save = async () => {
    try {
      await dispatch(updateSection({ id: selected.id, ...form })).unwrap();
      enqueueSnackbar(
        `"${selected.label}" saved. Live within ${revalidateSeconds} seconds.`,
        { variant: 'success', autoHideDuration: 6000 }
      );
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save the section', { variant: 'error' });
      (err?.errors || []).slice(1).forEach((e) => enqueueSnackbar(e, { variant: 'error' }));
    }
  };

  if (!form || !selected) return null;

  const headingPreview = [form.heading, form.headingAccent, form.headingSuffix]
    .filter(Boolean)
    .join(' ');

  return (
    <Grid container spacing={2}>
      {/* ---------------------------- section list ---------------------------- */}
      <Grid item xs={12} md={3}>
        <Card sx={{ p: 1 }}>
          <List dense disablePadding>
            {sections.map((section) => (
              <ListItemButton
                key={section.id}
                selected={section.id === selectedId}
                onClick={() => setSelectedId(section.id)}
                sx={{ borderRadius: 1 }}
              >
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {section.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {section.blocks?.length
                      ? `${section.blocks.length} item${section.blocks.length === 1 ? '' : 's'}`
                      : 'copy only'}
                  </Typography>
                </Stack>
                {!section.isVisible && (
                  <Chip size="small" label="Hidden" sx={{ height: 18 }} />
                )}
              </ListItemButton>
            ))}
          </List>
        </Card>
      </Grid>

      {/* ------------------------------- editor ------------------------------- */}
      <Grid item xs={12} md={9}>
        <Card sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">{selected.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                Section key: {selected.keyName}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={form.isVisible ? 'Shown on the page' : 'Hidden from the page'}>
                <Switch
                  checked={form.isVisible}
                  onChange={(e) => set({ isVisible: e.target.checked })}
                />
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                onClick={save}
                disabled={saving}
                startIcon={<Icon icon="mdi:content-save" width={16} />}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>

          {!form.isVisible && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This whole section is hidden. Visitors will not see it at all.
            </Alert>
          )}

          {/* ---- heading ---- */}
          <Typography variant="subtitle2" gutterBottom>
            Heading
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Before"
                value={form.heading}
                onChange={(e) => set({ heading: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Highlighted"
                value={form.headingAccent}
                onChange={(e) => set({ headingAccent: e.target.value })}
                helperText="Rendered in the brand gradient"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="After"
                value={form.headingSuffix}
                onChange={(e) => set({ headingSuffix: e.target.value })}
              />
            </Grid>
          </Grid>

          {headingPreview && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.neutral',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Reads as
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                {form.heading}{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(120deg, #7A4A9C, #2C5AA0 52%, #1C9BD8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {form.headingAccent}
                </Box>{' '}
                {form.headingSuffix}
              </Typography>
            </Box>
          )}

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Eyebrow"
                value={form.eyebrow}
                onChange={(e) => set({ eyebrow: e.target.value })}
                helperText="The small pill above the heading"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Button label"
                value={form.ctaLabel}
                onChange={(e) => set({ ctaLabel: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Button link"
                value={form.ctaHref}
                onChange={(e) => set({ ctaHref: e.target.value })}
                helperText="Empty opens the assessment"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Body copy"
                value={form.subheading}
                onChange={(e) => set({ subheading: e.target.value })}
              />
            </Grid>
            {(extraFields || []).map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  value={form.extra?.[field.key] ?? ''}
                  onChange={(e) => setExtra(field.key, e.target.value)}
                  helperText={field.helper}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Section image
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Leave empty to keep the built-in illustration.
          </Typography>
          <ImageField
            size={72}
            folderName="landing/sections"
            label="Image URL"
            value={form.media?.url || ''}
            imageKey={form.media?.key}
            onChange={(url, key) =>
              set({ media: url ? { ...(form.media || {}), url, key } : null })
            }
          />
          {form.media?.url && (
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              label="Alt text"
              value={form.media?.alt ?? ''}
              onChange={(e) => set({ media: { ...form.media, alt: e.target.value } })}
              helperText="Describe what the picture shows. Read by screen readers and by image search. Leave empty only if the image is purely decorative."
            />
          )}

          {/* ---- blocks ---- */}
          {(form.blocks.length > 0 || selected.blocks?.length > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="subtitle2">Items</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Order sets the words, not the look — each position keeps its own colour
                    and shape from the design.
                  </Typography>
                </Box>
                <Button size="small" startIcon={<Icon icon="mdi:plus" width={16} />} onClick={addBlock}>
                  Add item
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {form.blocks.map((block, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Stack alignItems="center" sx={{ pt: 0.5 }}>
                        <IconButton size="small" onClick={() => moveBlock(index, -1)} disabled={index === 0}>
                          <Icon icon="mdi:chevron-up" width={16} />
                        </IconButton>
                        <Chip size="small" label={index + 1} sx={{ height: 20 }} />
                        <IconButton
                          size="small"
                          onClick={() => moveBlock(index, 1)}
                          disabled={index === form.blocks.length - 1}
                        >
                          <Icon icon="mdi:chevron-down" width={16} />
                        </IconButton>
                      </Stack>

                      <Grid container spacing={1.5} sx={{ flex: 1 }}>
                        {groups && (
                          <Grid item xs={12} sm={3}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Shows in"
                              SelectProps={{ native: true }}
                              value={block.extra?.group || groups[0].value}
                              onChange={(e) =>
                                setBlock(index, {
                                  extra: { ...(block.extra || {}), group: e.target.value },
                                })
                              }
                            >
                              {groups.map((g) => (
                                <option key={g.value} value={g.value}>
                                  {g.label}
                                </option>
                              ))}
                            </TextField>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={groups ? 5 : 6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Title"
                            value={block.title || ''}
                            onChange={(e) => setBlock(index, { title: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Tag"
                            value={block.eyebrow || ''}
                            onChange={(e) => setBlock(index, { eyebrow: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Link"
                            value={block.href || ''}
                            onChange={(e) => setBlock(index, { href: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            label="Description"
                            value={block.body || ''}
                            onChange={(e) => setBlock(index, { body: e.target.value })}
                          />
                        </Grid>
                        {(block.valueText !== null && block.valueText !== undefined) ||
                        selected.keyName === 'stats' ? (
                          <>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Number"
                                value={block.valueText || ''}
                                onChange={(e) => setBlock(index, { valueText: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Suffix"
                                value={block.valueSuffix || ''}
                                onChange={(e) => setBlock(index, { valueSuffix: e.target.value })}
                              />
                            </Grid>
                          </>
                        ) : null}
                        {block.icon !== null && block.icon !== undefined && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Icon"
                              value={block.icon || ''}
                              onChange={(e) => setBlock(index, { icon: e.target.value })}
                              helperText="Iconify name, e.g. ph:brain-duotone"
                              InputProps={{
                                startAdornment: block.icon ? (
                                  <Box sx={{ mr: 1, display: 'flex' }}>
                                    <Icon icon={block.icon} width={20} />
                                  </Box>
                                ) : null,
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>

                      <IconButton size="small" onClick={() => removeBlock(index)}>
                        <Icon icon="mdi:trash-can-outline" width={18} />
                      </IconButton>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

export default SectionPanel;
